import { useEffect, useMemo, useState } from "react";
import { Search, Globe2, UserPlus } from "lucide-react";
import { api } from "../lib/api";
import RequestContactDialog from "../components/RequestContactDialog";
import { getAvatarSrc } from "../lib/avatar";

export default function Directory() {
  const [q, setQ] = useState("");
  const [profession, setProfession] = useState("");
  const [residence, setResidence] = useState("all");
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    setLoading(true);

    const params = {};

    if (q) params.q = q;
    if (profession) params.profession = profession;
    if (residence !== "all") params.residence = residence;

    api
      .get("/members/directory", { params })
      .then(({ data }) => setMembers(data))
      .finally(() => setLoading(false));
  }, [q, profession, residence]);

  const count = useMemo(() => members.length, [members]);

  const sortedMembers = useMemo(() => {
    return [...members].sort((a, b) =>
      formatName(a.name).localeCompare(
        formatName(b.name),
        undefined,
        {
          sensitivity: "base",
          numeric: true,
        }
      )
    );
  }, [members]);

  return (
    <div data-testid="directory-page">
      <section className="container-px pt-16 md:pt-24 pb-10">
        <div className="max-w-3xl">
          <div className="overline mb-6">Members Directory</div>

          <h1 className="font-heading text-5xl md:text-6xl font-light tracking-tight text-ink leading-[1.05]">
            Meet the <span className="italic text-brand">forum</span>.
          </h1>

          <p className="mt-6 text-lg text-ink-soft leading-relaxed">
            A curated directory of approved members. To protect privacy,
            contact details are hidden — request access and our admin
            will connect you.
          </p>
        </div>
      </section>

      <section className="container-px pb-6">
        <div className="flex flex-col gap-3">
          <div className="flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
            <div className="relative flex-1 max-w-xl">
              <Search
                className="w-4 h-4 text-ink-muted absolute left-4 top-1/2 -translate-y-1/2"
                strokeWidth={1.5}
              />

              <input
                className="input-premium pl-11"
                placeholder="Search by name or business details…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                data-testid="directory-search-input"
              />
            </div>

            <div
              className="flex gap-2"
              data-testid="directory-filter"
            >
              {[
                { k: "all", label: "All" },
                { k: "indian", label: "Indian" },
                { k: "nri", label: "NRI" },
              ].map((f) => (
                <button
                  key={f.k}
                  onClick={() => setResidence(f.k)}
                  className={`px-5 py-2.5 rounded-full text-sm font-medium transition ${residence === f.k
                      ? "bg-ink text-white"
                      : "bg-white border border-black/10 text-ink-soft hover:border-brand"
                    }`}
                  data-testid={`directory-filter-${f.k}`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>

          <div className="flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
            <div className="relative flex-1 max-w-xl">
              <Search
                className="w-4 h-4 text-ink-muted absolute left-4 top-1/2 -translate-y-1/2"
                strokeWidth={1.5}
              />

              <input
                className="input-premium pl-11"
                placeholder="Filter by profession…"
                value={profession}
                onChange={(e) => setProfession(e.target.value)}
                data-testid="directory-profession-filter-input"
              />
            </div>

            <button
              type="button"
              onClick={() => {
                setQ("");
                setProfession("");
                setResidence("all");
              }}
              className="btn-ghost md:self-end"
            >
              Clear filters
            </button>
          </div>
        </div>

        <div className="mt-4 text-sm text-ink-muted">
          {loading
            ? "Loading directory…"
            : `${count} approved member${count === 1 ? "" : "s"}`}
        </div>
      </section>

      <section className="container-px pb-24">
        {!loading && members.length === 0 && (
          <div className="card-premium text-center py-16">
            <div className="font-heading text-2xl text-ink">
              No approved members yet
            </div>

            <p className="text-ink-soft mt-3">
              Once the council approves applications,
              members will appear here.
            </p>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {sortedMembers.map((m, i) => (
            <article
              key={m.id}
              className="card-premium flex flex-col"
              data-testid={`directory-card-${i}`}
            >
              <div className="flex items-start gap-4">
                {getAvatarSrc(m) ? (
                  <img
                    src={getAvatarSrc(m)}
                    alt={m.name}
                    className="w-14 h-14 rounded-2xl object-contain bg-white border border-black/10 shrink-0"
                    data-testid={`directory-avatar-${i}`}
                  />
                ) : (
                  <div className="w-14 h-14 rounded-2xl bg-brand/10 text-brand flex items-center justify-center font-heading text-xl font-semibold shrink-0">
                    {initials(m.name)}
                  </div>
                )}

                <div className="min-w-0">
                  <h3 className="font-heading text-xl text-ink truncate">
                    {formatName(m.name)}
                  </h3>

                  <div className="text-sm text-brand font-medium">
                    {m.profession}
                  </div>
                </div>
              </div>

              <p className="mt-5 text-ink-soft leading-relaxed text-sm line-clamp-4">
                {m.business_details}
              </p>

              <div className="mt-5 flex flex-wrap gap-3 text-xs text-ink-muted">
                <span className="inline-flex items-center gap-1">
                  <Globe2 className="w-3 h-3" />

                  {m.residence_type === "indian"
                    ? "Indian Resident"
                    : "NRI"}
                </span>
              </div>

              <button
                onClick={() => setSelected(m)}
                className="btn-secondary mt-6 w-full"
                data-testid={`directory-request-contact-btn-${i}`}
              >
                <UserPlus className="w-4 h-4" />
                Request contact access
              </button>
            </article>
          ))}
        </div>
      </section>

      <RequestContactDialog
        open={!!selected}
        onOpenChange={(o) => !o && setSelected(null)}
        member={selected}
      />
    </div>
  );
}

function initials(name = "") {
  return name
    .split(" ")
    .slice(0, 2)
    .map((p) => p[0])
    .join("")
    .toUpperCase();
}

function formatName(name = "") {
  return name
    .toLowerCase()
    .split(" ")
    .filter(Boolean)
    .map(
      (word) =>
        word.charAt(0).toUpperCase() + word.slice(1)
    )
    .join(" ");
}